import React from "react"
import ColorCode2Rgba from '../color-code'
import { Bar, HorizontalBar, Line, Scatter, Doughnut, Pie, Polar, Radar } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import lodash from 'lodash'

const Chart = {
    Bar: ({title, data, labels, legend, colors=null, backgroundColorAlpha=1, unit='', ticks={}, datalabels={}, size="s", datasource="", showDataTable=false, options={}, barPercentage=0.9, categoryPercentage=0.8, topInfo="", bottomInfo="", category="", changeMaxColor=false}) => {
        colors = colors === null ? barColors(category, data, changeMaxColor) : colors
        const formatData = separateLabelsData(data, labels)
        const chartData = {
            labels: formatData.labels,
            datasets: formatDataset({data: formatData.data, colors, backgroundColorAlpha, legend, barPercentage, categoryPercentage})
        }
        const defaultOptions = barDefaultOptions(unit, ticks, datalabels, false, false)
        return chartBlock({tag: Bar, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },

    BarH: ({title, data, labels, legend, colors=null, backgroundColorAlpha=1, unit='', ticks={}, datalabels={}, size="s", datasource="", showDataTable=false, options={}, barPercentage=0.9, categoryPercentage=0.8, topInfo="", bottomInfo="", category="", changeMaxColor=false}) => {
        colors = colors === null ? barColors(category, data, changeMaxColor) : colors
        const formatData = separateLabelsData(data, labels)
        const chartData = {
            labels: formatData.labels,
            datasets: formatDataset({data: formatData.data, colors, backgroundColorAlpha, legend, barPercentage, categoryPercentage})
        }
        const defaultOptions = barDefaultOptions(unit, ticks, datalabels, false, true)
        return chartBlock({tag: HorizontalBar, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },

    StachedBar: ({title, data, labels, legend, colors=null, backgroundColorAlpha=1, unit='', ticks={}, datalabels={}, size="s", datasource="", showDataTable=false, options={}, barPercentage=0.9, categoryPercentage=0.8, topInfo="", bottomInfo="", category=""}) => {
        const formatData = separateLabelsStackedData(data, labels)
        const chartData = {labels: formatData.labels, datasets: []}
        chartData.datasets = formatDataset({data: formatData.data, colors, backgroundColorAlpha, legend, barPercentage, categoryPercentage})
        const defaultOptions = barDefaultOptions(unit, {...defaultStatedBarTicks, ...ticks}, datalabels, true, false)
        return chartBlock({tag: Bar, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },

    StachedBarH: ({title, data, labels, legend, colors, backgroundColorAlpha=1, unit='', ticks={}, datalabels={}, size="s", datasource="", showDataTable=false, options={}, barPercentage=0.9, categoryPercentage=0.8, topInfo="", bottomInfo="", category=""}) => {
        const formatData = separateLabelsStackedData(data, labels)
        const chartData = {labels: formatData.labels, datasets: []}
        chartData.datasets = formatDataset({data: formatData.data, colors, backgroundColorAlpha, legend, barPercentage, categoryPercentage})
        const defaultOptions = barDefaultOptions(unit, {...defaultStatedBarTicks, ...ticks}, datalabels, true, true)
        return chartBlock({tag: HorizontalBar, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },

    Line: ({title, data, labels, legend, colors=null, borderWidth=1, pointRadius=0, unit='', ticks={}, datalabels={}, size="s", datasource="", showDataTable=false, options={}, topInfo="", bottomInfo="", category=""}) => {
        const dataLength = Array.isArray(data[0]) ? data.length : 1
        colors = colors === null ? getLineColors(category, dataLength) : colors
        const formatData = separateLabelsData(data, labels)
        const chartData = {
            labels: formatData.labels,
            datasets: formatDataset({data: formatData.data, colors, legend, borderWidth, pointRadius, ...lineDefaultDatasetProps})
        }
        const defaultOptions = {
            scales: {
                xAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            padding: 5,
                            unit
                        },
                        gridLines: {
                            zeroLineColor: 'rgba(0, 0, 0, 0.1)'
                        },
                    },
                ],
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            padding: 5,
                            ...ticks
                        },
                        gridLines: {
                            drawTicks: false,
                        },
                    },
                ],
            },
            datalabels
        }
        if (!legend) defaultOptions['legend'] = false
        return chartBlock({tag: Line, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },

    BarLine: ({title, barData, lineData, labels, barColors=null, lineColors=null, barBackgroundColorAlpha=1, unit='', barTicks={}, lineTicks={}, size="s", datasource="", showDataTable=false, options={}, barPercentage=0.9, categoryPercentage=0.8, topInfo="", bottomInfo="", category=""}) => {
        const chartData = {labels: (labels ? labels : []), datasets: []}
        barColors = barColors === null ? getColors(category, Object.keys(barData).length) : barColors
        const lineDataLength = Object.keys(lineData).length
        lineColors = lineColors === null ? getLineColors(category, lineDataLength) : lineColors

        const barFormatData = separateLabelsData(barData, [])
        const lineFormatData = separateLabelsData(lineData, [])
        const barDatasets = formatDataset({data: barFormatData.data, colors: barColors, backgroundColorAlpha: barBackgroundColorAlpha, legend: barFormatData.labels, type: 'bar', yAxisID: 'y-axis-bar', order: 1, barPercentage, categoryPercentage})
        const lineDatasets = formatDataset({data: lineFormatData.data, colors: lineColors, legend: lineFormatData.labels, type: 'line', yAxisID: 'y-axis-line', order: 0, ...lineDefaultDatasetProps})
        chartData.datasets = [...barDatasets, ...lineDatasets]

        const defaultOptions = {
            scales: {
                xAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            padding: 5,
                            unit
                        },
                        gridLines: {
                            drawOnChartArea: false,
                        },
                    },
                ],
                yAxes: [
                    {
                        id: 'y-axis-bar',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            padding: 5,
                            ...barTicks
                        },
                        gridLines: {
                            drawTicks: false,
                        },
                    },
                    {
                        id: 'y-axis-line',
                        position: 'right',
                        ticks: {
                            beginAtZero: true,
                            padding: 5,
                            ...lineTicks
                        },
                        gridLines: {
                            drawTicks: false,
                        },
                    },
                ],
            },
            legend :{
                reverse: true
            }
        }
        return chartBlock({tag: Bar, chartData, defaultOptions, options, showDataTable, legend: [...barFormatData.labels, ...lineFormatData.labels], title, size, datasource, topInfo, bottomInfo})
    },

    Pie: ({title, data, labels, legend, colors=null, backgroundColorAlpha=1, unit='', datalabels={}, size="s", datasource="", showDataTable=false, options={}, topInfo="", bottomInfo="", category=""}) => {
        colors = colors === null ? getColors(category, data.length, true) : colors
        const formatData = separateLabelsData(data, labels)
        const chartData = {
            labels: formatData.labels,
            datasets: formatDataset({data: formatData.data, colors, backgroundColorAlpha, legend})
        }
        datalabels = {
            displayValue: 5,
            disits: 1,
            color: '#4A4A4A',
            font: {
                weight: 'bold'
            },
            unit,
            ...datalabels
        }
        const defaultOptions = {
            datalabels
        }
        return chartBlock({tag: Pie, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },

    Doughnut: ({title, data, labels, legend, colors, backgroundColorAlpha=1, unit='', datalabels={}, size="s", datasource="", showDataTable=false, options={}, topInfo="", bottomInfo="", category=""}) => {
        const formatData = separateLabelsData(data, labels)
        const chartData = {
            labels: formatData.labels,
            datasets: formatDataset({data: formatData.data, colors, backgroundColorAlpha, legend})
        }
        if (Object.keys(datalabels).length === 0) {
            datalabels = {
                displayValue: 5,
                disits: 1,
                color: '#fff',
                unit
            }
        }
        const defaultOptions = {
            datalabels
        }
        return chartBlock({tag: Doughnut, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo})
    },
    
    Radar: ({title, data, labels, color, backgroundColorAlpha=1, borderWidth=1, ticks={}, datalabels={}, size="s", datasource="", showDataTable=false, options={}, topInfo="", bottomInfo="", category=""}) => {
        const formatData = separateLabelsData(data, labels)
        const chartData = {
            labels: formatData.labels,
            datasets: formatDataset({data: formatData.data, colors: color, backgroundColorAlpha, borderWidth, pointRadius: 0})
        }
        const defaultOptions = {
            legend: false,
            scale: {
                ticks: {
                    beginAtZero: true,
                    ...ticks
                },
            },
            datalabels
        }
        return chartBlock({tag: Radar, chartData, defaultOptions, options, showDataTable, title, size, datasource, topInfo, bottomInfo})
    },
}

/* <- public ------------------------------------------------------- private -> */

const chartjsTag = (tag, data, options) => {
    const ChartjsTag = tag
    return (<ChartjsTag data={data} options={options} plugins={[ChartDataLabels]} />)
}

const chartBlock = ({tag, chartData, defaultOptions, options, showDataTable, legend, title, size, datasource, topInfo, bottomInfo}) => {
    const chartOptions = optionsMerge(defaultOptions, options, showDataTable, legend)
    const chartTag = chartjsTag(tag, chartData, chartOptions)
    const tableTag = chartDataTable(tag.name, showDataTable, chartData.datasets, chartData.labels)

    return (
        <div className={`c-section-figure c-section-figure-${size}`}>
            <span class="c-section-figure-title" dangerouslySetInnerHTML={{ __html:title}}/>
            {topInfo}
            <div>
                {chartTag}
            </div>
            <span class="source" dangerouslySetInnerHTML={{ __html:datasource}}/>
            {tableTag}
            {bottomInfo}
        </div>
    )
}

const separateLabelsData = (data, labels) => {
    const isArray = Array.isArray(data)
    return {data: !isArray ? Object.values(data) : data, labels: !isArray ? Object.keys(data) : labels}
}

const separateLabelsStackedData = (data, labels) => {
    labels = Array.isArray(labels) ? labels : []
    if (Array.isArray(data)) {
        return {data: data.map((value) => { return [value] }), labels}
    }
    const values = []
    for (let label in data) {
        labels.push(label)
        const barData = data[label]
        if (!Array.isArray(barData)) continue
        for (let i=0; i<barData.length; i++) {
            if (!values[i]) values[i] = []
            values[i].push(barData[i])
        }
    }
    return {data: values, labels}
}

const formatDataset = ({data, colors, legend, backgroundColorAlpha=1, ...props}) => {
    const datasets = []
    if (!Array.isArray(data[0])) data = [data]
    if (!Array.isArray(colors)) colors = [colors]
    if (!legend && !Array.isArray(legend)) legend = [legend]
    for (let i=0; i<data.length; i++) {
        const backgroundColor = (() => {
            const colorCodes = ColorCode2Rgba((data.length === 1 ? colors : colors[i]), backgroundColorAlpha)
            return colorCodes.length === 1 ? colorCodes[0] : colorCodes
        })()
        const borderColor = (() => {
            const borderColors = data.length === 1 ? colors : colors[i]
            return borderColors.length === 1 ? borderColors[0] : borderColors
        })()
        const dataset = {
            data: data[i],
            backgroundColor: backgroundColor,
            borderColor: borderColor,
            borderWidth: 1
        }
        if (legend) dataset['label'] = legend[i]
        datasets.push({...dataset, ...props})
    }
    return datasets
}

const optionsMerge = (defaultOptions, mergeOption, showDataTable, legend=false, base=null) => {
    const defaultBase = {
        legend: {
            display: Boolean(legend),
            position: 'bottom'
        },
        tooltips: {enabled: false},
        hover: {mode: null},
        plugins: {
            datalabels: {
                display: false
            }
        },
        responsive: true,
        maintainAspectRatio: false
    }
    base = base === null ? defaultBase : base
    const options = lodash.merge({}, base, defaultOptions, mergeOption)
    if (showDataTable) options.legend = false
    if (options.scales) {
        if (options.scales.xAxes) options.scales.xAxes =ticksOptions(options.scales.xAxes)
        if (options.scales.yAxes) options.scales.yAxes =ticksOptions(options.scales.yAxes)
    }
    if (options.datalabels) {
        options.plugins.datalabels = {
            ...options.plugins.datalabels,
            ...datalabelsOptions(options.datalabels)
        }
        delete options.datalabels
    }
    return options
}

const ticksOptions = (axes) => {
    for (const axis of axes) {
        if (axis.ticks) {
            if (axis.ticks.rate || axis.ticks.unit || axis.ticks.disits) {
                const rate = axis.ticks.rate ? axis.ticks.rate : 0
                const unit = axis.ticks.unit ? (axis.ticks.unit.value ? axis.ticks.unit.value : axis.ticks.unit) : ''
                const unitView = axis.ticks.unit && axis.ticks.unit.view ? axis.ticks.unit.view : false
                const disits = axis.ticks.disits ? axis.ticks.disits : 0
                axis.ticks.callback = (value, index, values) => {
                    const val = rate > 0 ? value * rate : value
                    const disitValue = disits > 0 ? val.toFixed(disits) : val
                    const unitIndex = (() => {
                        if (!unitView) return -1
                        if (unitView === 'max') {
                            return 0
                        } else if (unitView === 'min') {
                            return values.length - 1
                        }
                        return -1
                    })()
                    const viewUnit = unitIndex < 0 ? true : (index === unitIndex ? true : false)
                    return viewUnit ? unitView === 'max' ? [unit, disitValue, ''] : `${disitValue}${unit}` : disitValue
                }
                delete axis.ticks.rate
                delete axis.ticks.unit
                delete axis.ticks.disits
            }
        }
    }
    return axes
}

const datalabelsOptions = (datalabels) => {
    if (datalabels.displayValue > 0) {
        const value = datalabels.displayValue
        datalabels.display = (context) => {
            return context.dataset.data[context.dataIndex] > value ? true : false
        }
        delete datalabels.displayValue
    }
    if (datalabels.disits || datalabels.unit) {
        const disits = datalabels.disits
        const unit = datalabels.unit ? datalabels.unit : ''
        datalabels.formatter = (value, ctx) => {
            return `${value.toFixed(disits)}${unit}`
        }
        delete datalabels.disits
        delete datalabels.unit
    }
    return datalabels
}

const barDefaultOptions = (unit, ticks, datalabels, stacked, isH) => {
    if (Object.keys(datalabels).length === 0) {
        if (stacked) {
            datalabels = {
                displayValue: 5,
                disits: 1,
                color: 'white',
            }
        } else {
            datalabels = {
                display: false
            }
        }
    }

    const hAxes = [
        {
            stacked,
            ticks: {
                beginAtZero: true,
                padding: 5,
            },
            gridLines: {
                drawOnChartArea: false,
                drawTicks: false,
            },
        }
    ]

    const vAxes = [
        {
            stacked,
            ticks: {
                beginAtZero: true,
                unit,
                ...ticks
            },
            gridLines: {
                drawOnChartArea: !stacked,
            },
        },
    ]

    return {
        scales: {
            xAxes: (isH ? vAxes : hAxes),
            yAxes: (isH ? hAxes : vAxes),
        },
        datalabels
    }
}

const defaultStatedBarTicks = {
    min: 0,
    max: 100,
    stepSize: 20,
}

const lineDefaultDatasetProps = {
    fill: false,
    lineTension: 0,
    borderWidth: 3,
    pointRadius: 0
}

const chartDataTable = (tagName, showDataTable, datasets, labels) => {
    if (!showDataTable) return false
    const rows = []
    const thStyle = {
        border: "1px solid #000",
        padding: "5px",
        fontWeight: "bold",
        textAlignLast: 'justify',
    }
    const labelsTargets = ['Pie', 'Doughnut', 'Radar']
    const theadLabels = labelsTargets.includes(tagName) && Array.isArray(labels) ? [...labels] : []

    if (theadLabels.length > 0 && datasets[0].label) theadLabels.unshift('')
    const header = (theadLabels.length > 0) ? 
        (<thead>
        <tr>
            {theadLabels.map((label, index) => {
            return (
                <th style={thStyle} key={`chartdatatable-label-${index}`}>
                {Array.isArray(datasets[0].borderColor) && datasets[0].borderColor[index] ? chartDataTableHeader(datasets[0].borderColor[index], label) : label}
                </th>
            )
            })}
        </tr>
        </thead>)
    : false

    let datasetIndex = 0
    for (const dataset of datasets) {
        rows.push(
        (<tr key={`chartdatatable-${datasetIndex}`}>
            {(dataset.label ? (
            <th style={thStyle}>
                {chartDataTableHeader(dataset.borderColor, dataset.label)}
            </th>
            ) : false)}
            {dataset.data.map((value, index) => {
            return (
                <td key={`${dataset.label}-${index}`} style={{
                border: "1px solid #000",
                padding: "5px",
                textAlign: "right",
                }}>
                {value}
                </td>
            )})}
        </tr>)
        )
        datasetIndex++
    }
    return (
        <table style={{
            border: "1px solid #000",
            width: "100%",
        }}>
            {header}
            <tbody>
                {rows}
            </tbody>
        </table>
    )
}

const chartDataTableHeader = (color, label) => {
    return (
        <React.Fragment>
            <span style={{
                color: color,
                fontSize: "1.2em",
            }}>
                ■
            </span>
            {label}
        </React.Fragment>
    )
}

// const defaultColor = "#96cfff"
// const maxColor = "#ffd496"
// const minColor = "#f7f7f7"
const defaultDeepColor = "#1a58ff"
const maxDeepColor = "#fe590f"
const minDeepColor = "#4A4A4A"

const categoryColors = {
    voice: {
        defaultColor: "#FFF1A8",
        maxColor: "#FFDD27",
        minColor: "#f7f7f7",
        lineColor: "#e0a810"
    },
    active: {
        defaultColor: "#E0F8FD",
        maxColor: "#98E8F7",
        minColor: "#f7f7f7",
        lineColor: "#1FB0DB"
    },
    future: {
        defaultColor: "#ECEBFF",
        maxColor: "#BFBCFF",
        minColor: "#f7f7f7",
        lineColor: "#3B34D1"
    },
    qa: {
        defaultColor: "#ECF7DC",
        maxColor: "#C1E58B",
        minColor: "#f7f7f7",
        lineColor: "#80BF2A"
    },
    top: {
        defaultColor: "#FFDDEB",
        maxColor: "#FF92C4",
        minColor: "#f7f7f7",
        lineColor: "#FF4D9F"
    },
}

const barColors = (category, data, changeMaxColor) => {
    const { defaultColor, maxColor } = categoryColors[category]
    if (!changeMaxColor) return defaultColor
    const maxValue = Math.max(...data)
    const maxIndex = data.indexOf(maxValue)
    const colors = []
    for (let i=0; i<data.length; i++) {
        colors.push((i === maxIndex ? maxColor : defaultColor))
    }
    return colors
}

const getLineColors = (category, dataLength) => {
    if (dataLength === 1) return categoryColors[category].lineColor
    const colors = getColors(category, dataLength - 1)
    return [categoryColors[category].lineColor, ...colors]
}

const getColors = (category, dataLength, isChangeFirst=false, isDeep=false) => {
    const { defaultColor, maxColor, minColor } = categoryColors[category]
    const defaultColorCode = isDeep ? defaultDeepColor : defaultColor
    const maxColorCode = isDeep ? maxDeepColor : maxColor
    const firstColor = isChangeFirst ? maxColorCode : defaultColorCode
    const secondColor = isChangeFirst ? defaultColorCode : maxColorCode
    const lastColor = isDeep ? minDeepColor : minColor
    const colors = []
    for (let i=0; i<dataLength; i++) {
        if (i < 4) {
            colors.push((i === 0 ? firstColor : i === 1 ? secondColor : lastColor))
        } else {

        }
    }
    return colors
}

export {Bar, HorizontalBar, Line, Scatter, Doughnut, Pie, Polar, Radar}
export default Chart